import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';
import { DropdownItemComponent } from '../../../components/dropdown/dropdown-item.component';
import { DropdownToggleDirective } from '../../../components/dropdown/dropdown-toggle.directive';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AccountGroup } from '../../../domain/public-api';
import { AccountContextService } from '../services/account-context.service';
import { AccountGroupLabelComponent } from './account-group-label.component';

/**
 * Account Group Selector Component
 * Account group selector component consisting of a dropdown of account groups, together with localStorage component to
 * save selected account.
 *
 * Params:
 *
 * @param {Array} accountGroups List of account groups to show in the dropdown
 * @param {String} initAccountGroupId optional Account group id to select in dropdown.
 * @param {function} initAccountGroup optional Callback function is called when initial value for selected account group is set
 * @param {String} showPlaceholder optional Whether to show 'Please Select' placeholder
 * @param {number}
 *
 * @example
 * <td-wb-account-group-selector
 [accountGroups]="accountGroups"
 [(accountGroup)]="accountGroup"
 [initAccountGroupId]="initAccountGroupId"
 (initAccountGroupChange)="onInitAccountGroup($event)"
 [showPlaceholder]="true"
 ngDefaultControl>
 </td-wb-account-group-selector>
 */
@Component({
  selector: 'td-wb-account-group-selector',
  templateUrl: './account-group-selector.component.html',
  styleUrls: ['./account-group-selector.component.scss'],
  imports: [
    DropdownComponent,
    FormsModule,
    TranslatePipe,
    AccountGroupLabelComponent,
    DropdownItemComponent,
    DropdownToggleDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountGroupSelectorComponent implements OnInit, OnChanges {

  public static readonly DEFAULT_DROP_DOWN_MAX_SIZE: number = 10;

  /*
   * List of account groups to show in the dropdown
   */
  @Input() accountGroups: Array<AccountGroup>;
  /*
   * Maximum size of drop down options to show
   */
  @Input() size: number = AccountGroupSelectorComponent.DEFAULT_DROP_DOWN_MAX_SIZE;
  /*
   * Whether to show 'Please Select' placeholder
   */
  @Input() showPlaceholder: boolean;
  /*
   * Account group id to select in dropdown.
   */
  @Input() initAccountGroupId: string;

  @Input() accountGroup: AccountGroup;
  @Output() accountGroupChange: EventEmitter<AccountGroup> = new EventEmitter<AccountGroup>();

  // initial account group change event
  @Output() initAccountGroupChange: EventEmitter<AccountGroup> = new EventEmitter<AccountGroup>();

  constructor(private accountContextService: AccountContextService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.initSelectedAccountGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.accountGroup !== undefined && changes.accountGroup.currentValue !== undefined) {
      this.storeAccountGroupId(changes.accountGroup.currentValue);
    }
  }

  storeAccountGroupId(accountGroup: AccountGroup): void {
    if (accountGroup !== undefined) {
      this.accountContextService.setSelectedAccountGroupId(accountGroup.groupId);
    }
  }

  onModelChange($event: AccountGroup): void {
    this.accountGroupChange.next($event);
  }

  private initSelectedAccountGroup(): void {
    this.accountContextService.resolveAccountGroup(this.accountGroups, this.initAccountGroupId)
      .subscribe((accountGroup: AccountGroup): void => {
        Promise.resolve().then((): void => {
          this.accountGroup = accountGroup;
          this.accountGroupChange.emit(this.accountGroup);
          this.storeAccountGroupId(accountGroup);
          if (this.initAccountGroupChange !== undefined) {
            this.initAccountGroupChange.next(this.accountGroup);
          }
          this.changeDetectorRef.markForCheck();
        });
      });
  }
}
